import React from 'react';
import { motion } from 'framer-motion';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Instagram,
  Heart,
  ArrowUp
} from 'lucide-react';

// TikTok Icon Component (since it's not in lucide-react)
const TikTokIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-.04-.1z"/>
  </svg>
);

export default function Footer() {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const socialLinks = [
    { icon: Instagram, href: 'https://www.instagram.com/kkn.karangmalang25', label: 'Instagram' },
    { icon: TikTokIcon, href: 'https://www.tiktok.com/@kkn.karangmalang', label: 'TikTok' },
  ];

  return (
    <footer className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 text-white overflow-hidden transition-colors duration-300">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5 dark:opacity-10">
        <div 
          className="w-full h-full"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      </div>

      <div className="relative">
        {/* Main Footer Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            
            {/* Logo & Description Section - Left */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-4"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="flex items-center justify-center">
                  <img 
                    src="/assets/logo/logo_kkn_putih.png" 
                    alt="Logo KKN" 
                     className="h-14 w-14 sm:h-16 sm:w-16 lg:h-20 lg:w-20 object-contain drop-shadow-lg transition-all duration-300"
                  />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-white">KKN DESA KARANGMALANG</h3>
                  <p className="text-blue-200 dark:text-blue-300 text-sm">Universitas Jenderal Soedirman</p>
                </div>
              </div>
              
              <p className="text-gray-300 dark:text-gray-400 mb-6 leading-relaxed text-sm">
                KKN Periode Juli-Agustus 2025<br/>
                Desa Karangmalang, Kecamatan Bobotsari<br/>
                Kabupaten Purbalingga, Jawa Tengah<br/>
                Indonesia
              </p>

              
            </motion.div>

            {/* Center Menu Sections */}
            <div className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-3 gap-8">
              
              {/* Program Links */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <h4 className="text-lg font-semibold mb-4 text-white">Program</h4>
                <ul className="space-y-2">
                  {['Beranda', 'Program', 'Tim KKN', 'Dokumentasi'].map((item, index) => (
                    <motion.li
                      key={item}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 + 0.3 }}
                    >
                      <a
                        href={`/${item.toLowerCase()}`}
                        className="text-gray-300 hover:text-blue-300 transition-colors duration-200 text-sm block hover:translate-x-1 transform transition-transform"
                      >
                        {item}
                      </a>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>

              {/* Desa Links */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <h4 className="text-lg font-semibold mb-4 text-white">Desa</h4>
                <ul className="space-y-2">
                  {['Profil Desa', 'Potensi Desa', 'Struktur Perangkat', 'Program Kegiatan'].map((item, index) => (
                    <motion.li
                      key={item}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 + 0.4 }}
                    >
                      <a
                        href="/"
                        className="text-gray-300 hover:text-blue-300 transition-colors duration-200 text-sm block hover:translate-x-1 transform transition-transform"
                      >
                        {item}
                      </a>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>

              {/* KKN Links */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <h4 className="text-lg font-semibold mb-4 text-white">KKN</h4>
                <ul className="space-y-2">
                  {['Struktur Organisasi', 'Program Kegiatan', 'Dokumentasi', 'Artikel'].map((item, index) => (
                    <motion.li
                      key={item}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 + 0.5 }}
                    >
                      <a
                        href="/"
                        className="text-gray-300 hover:text-blue-300 transition-colors duration-200 text-sm block hover:translate-x-1 transform transition-transform"
                      >
                        {item}
                      </a>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            </div>

            {/* Contact Section - Right */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="lg:col-span-2"
            >
              <h4 className="text-lg font-semibold mb-4 text-white">Kontak Kami</h4>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-2 flex-wrap">
                  <Phone className="h-4 w-4 text-blue-400 flex-shrink-0" />
                  <span className="text-gray-300 text-sm whitespace-nowrap">
                    +62 896-6968-5184 ( Firdian - Kormades )
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Mail className="h-4 w-4 text-blue-400 flex-shrink-0" />
                  <span className="text-gray-300 text-sm whitespace-nowrap">
                    kknkarangmalang505@gmail.com
                  </span>
                </div>
              </div>

              {/* Social Links */}
              <div className="flex space-x-3">
                {socialLinks.map((social, index) => {
                  const Icon = social.icon;
                  return (
                    <motion.a
                      key={social.label}
                      href={social.href}
                      initial={{ opacity: 0, scale: 0 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.1, duration: 0.3 }}
                      whileHover={{ scale: 1.1, y: -2 }}
                      className="h-10 w-10 bg-gradient-to-br from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 rounded-lg flex items-center justify-center transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/25"
                    >
                      <Icon className="h-5 w-5 text-white" />
                    </motion.a>
                  );
                })}
              </div>

             
             
            </motion.div>
          </div>
        </div>

        {/* Divider Line - Half width in center */}
        <div className="flex justify-center">
          <div className="w-1/2 h-px bg-gradient-to-r from-transparent via-slate-600 to-transparent"></div>
        </div>

        {/* Bottom Footer */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="flex items-center justify-center space-x-2 text-gray-400 dark:text-gray-500 text-sm"
            >
              <span>&copy; 2025 KKN Desa Karangmalang. All rights reserved.</span>
            </motion.div>
          </div>
        </div>
      </div>
    </footer>
  );
}